import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Sidebar } from "@/components/layout/Sidebar";
import { Topbar } from "@/components/layout/Topbar";
import { FloatingPostitProvider, useFloatingPostit } from "@/components/layout/FloatingPostitContext";
import { FloatingPostitOverlay } from "@/pages/operations/PostitsList";
import { AssistantChat } from "@/components/ui/AssistantChat";
import { MentionToastProvider } from "@/components/ui/MentionToast";

interface BreadcrumbContextValue {
  labels: Record<string, string>;
  setLabel: (path: string, label: string | null) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue>({
  labels: {},
  setLabel: () => {},
});

export function useBreadcrumbLabels() {
  return useContext(BreadcrumbContext);
}

function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [labels, setLabels] = useState<Record<string, string>>({});

  const value = useMemo<BreadcrumbContextValue>(
    () => ({
      labels,
      setLabel: (path, label) => {
        setLabels((current) => {
          if (!label) {
            if (!(path in current)) return current;
            const next = { ...current };
            delete next[path];
            return next;
          }
          if (current[path] === label) return current;
          return { ...current, [path]: label };
        });
      },
    }),
    [labels]
  );

  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

export function Layout() {
  // Gaveta de navegação — só tem efeito abaixo de md. No desktop a Sidebar
  // é estática (md:static) e este estado é ignorado.
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  // Fecha a gaveta a cada troca de rota. Sem isto, voltar/avançar no navegador
  // ou qualquer navegação programática deixaria a gaveta aberta sobre a página.
  useEffect(() => setNavOpen(false), [location.pathname]);

  return (
    <FloatingPostitProvider>
      <BreadcrumbProvider>
        <div className="flex h-screen overflow-hidden font-din text-gl-text">
          <Sidebar open={navOpen} onNavigate={() => setNavOpen(false)} />

          {/* Véu que fecha a gaveta ao tocar fora. md:hidden — nunca no desktop. */}
          {navOpen ? (
            <button
              type="button"
              onClick={() => setNavOpen(false)}
              className="md:hidden fixed inset-0 z-40 bg-black/50"
              aria-label="Fechar menu de navegação"
            />
          ) : null}

          {/* min-w-0 impede que conteúdo largo (o quadro) estique este container
              e empurre o layout para fora da tela no celular. */}
          <div className="flex-1 flex flex-col overflow-hidden min-w-0">
            <Topbar onOpenNav={() => setNavOpen(true)} />
            <main className="flex-1 overflow-y-auto p-4 md:p-8 bg-gl-bg-dark" id="main-content">
              <Outlet />
            </main>
          </div>
          <FloatingPostitOverlayPortal />
          <AssistantChat />
          <MentionToastProvider />
        </div>
      </BreadcrumbProvider>
    </FloatingPostitProvider>
  );
}

function FloatingPostitOverlayPortal() {
  const { floatingPostit, setFloatingPostit } = useFloatingPostit();
  if (!floatingPostit) return null;
  return (
    <FloatingPostitOverlay
      postit={floatingPostit}
      onClose={() => setFloatingPostit(null)}
    />
  );
}
